'use client';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useExplorer } from '@/hooks/useExplorer';
import { explorerAnalytics } from '@/lib/analytics';
import { createHighlighter } from 'shikiv2';
import { Box } from '@radix-ui/themes';
import { ClientAuthContext } from '@/auth/ClientAuthProvider';
import { loginWithRedirect } from '@/auth/hooks';
import { twMerge } from 'tailwind-merge';
import ChargebeeSiteLink from '@components/_components/ui/ChargebeeSiteLink';
import CopyButton from '@components/_components/ui/code-generator/CopyButton';

interface ResponseSampleContainerProps {
  sampleResponseDisclaimerVisibility: boolean;
}

export const ResponseSampleContainer: React.FC<
  ResponseSampleContainerProps
> = ({ sampleResponseDisclaimerVisibility }) => {
  const { responsePayload, endpoint } = useExplorer();
  const [codeFormatted, setCodeFormatted] = useState<string>('');
  const { isLoggedIn } = useContext(ClientAuthContext);

  const responseText = useMemo(() => {
    return responsePayload ? JSON.stringify(responsePayload, null, 2) : '';
  }, [responsePayload]);

  useEffect(() => {
    async function highlightCode() {
      const highlighter = await createHighlighter({
        themes: ['light-plus'],
        langs: ['json'],
      });
      const highlighted = highlighter.codeToHtml(responseText || '{}', {
        lang: 'json',
        theme: 'light-plus',
      });
      setCodeFormatted(highlighted);
    }

    highlightCode();
  }, [responseText]);

  return (
    <Box
      className={twMerge(
        'h-full flex flex-col',
        isLoggedIn ? 'drop-shadow-sm' : ''
      )}
    >
      <Box className="flex gap-2 w-full items-center px-1 p-3 text-sm bg-white drop-shadow-sm">
        <span className="font-semibold ml-2 text-xs uppercase">Response</span>
        {isLoggedIn ? (
          <ChargebeeSiteLink />
        ) : (
          sampleResponseDisclaimerVisibility && (
            <div className="flex items-center gap-1">
              <span className="text-gray-400">|</span>
              <span>This is a sample response.&nbsp;</span>
              <p className="flex items-center font-semibold">
                <span
                  onClick={loginWithRedirect}
                  className="underline-offset-1 underline cursor-pointer"
                >
                  Sign in
                </span>
                &nbsp;to play with your own data
              </p>
            </div>
          )
        )}
        <div className="flex-1" />
        <CopyButton
          content={responseText}
          className="text-gray-700 hover:text-gray-900"
          disabled={!responsePayload}
          onCopied={() => {
            explorerAnalytics.responseCopied({
              resource: endpoint?.name || '',
              endpoint: endpoint?.id || '',
              responseLength: responseText?.length || 0,
              isErrorResponse:
                responseText?.includes('api_error_code') ?? false,
            });
          }}
        />
      </Box>
      {responsePayload ? (
        <div className="flex-1 overflow-auto p-2 pt-5" data-pendo-mask>
          <div dangerouslySetInnerHTML={{ __html: codeFormatted }} />
        </div>
      ) : (
        <div className="overflow-y-auto flex-1 flex justify-center items-center text-gray-400 italic">
          Hit run above to see the response
        </div>
      )}
    </Box>
  );
};
